import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "./Modal";
import { phpCouponList } from "../data/api";
import { applyItemCoupon } from "../../../Redux/features/WebRegister/cartSlice";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";
import {
  filterItemLevelCoupons,
  isCouponValidToday,
  couponAppliesToLine,
  itemCouponIsPercent,
  computeItemCouponForApply,
  itemCouponLabel,
  toNum,
} from "../engine";

// Item-level coupon picker — port of Android's ItemCouponDialog + the applyItemCoupon validations
// (InStorePaymentFragment). Fetches the coupon list fresh from the SAME PHP endpoint Android uses
// on every open, shows type 1/2 coupons valid for today (enable/disable is online-only and ignored),
// and validates the selection against the line before dispatching. Messages match Android strings.
export default function ItemCouponModal({ orderId, onApplied, onClose }) {
  const dispatch = useDispatch();
  const line = useSelector((st) => st.webRegisterCart.lines.find((l) => l.orderId === orderId));
  const [list, setList] = useState(null); // null = loading
  const [search, setSearch] = useState("");

  useEffect(() => {
    let on = true;
    phpCouponList()
      .then((res) => {
        if (!on) return;
        const rows = res && Array.isArray(res.result) ? res.result : [];
        setList(filterItemLevelCoupons(rows));
      })
      .catch(() => {
        if (!on) return;
        setList([]);
        ToastifyAlert("Error fetching coupons", "error");
      });
    return () => {
      on = false;
    };
  }, []);

  const choose = (c) => {
    if (!line) return onClose();
    const isSpecific = String(c.type) === "2";
    // Android applyItemCoupon guard order:
    // 0. date validity (enable/disable intentionally NOT checked)
    if (!isCouponValidToday(c.date_valid, c.date_expire)) {
      return ToastifyAlert("This coupon is not valid for the current date.", "error");
    }
    // 1. specific coupon: the line must be one of the coupon's allowed products/variants
    if (isSpecific && !couponAppliesToLine(c, line)) {
      return ToastifyAlert("Coupon Invalid for current instance.", "error");
    }
    // 2. one item coupon per line; manual discount blocks a non-combinable coupon
    if (line.couponDiscountName) {
      return ToastifyAlert("A coupon is already applied on this item. Remove it before applying another.", "error");
    }
    const hasManual = toNum(line.manualDiscountAmt) > 0 || toNum(line.manualDiscountPercent) > 0;
    const canCombine = String(c.can_combine_discounts ?? c.canCombineDiscounts ?? "0") === "1";
    if (hasManual && !canCombine) {
      return ToastifyAlert("A discount is already applied on this item and this coupon can't be combined with it.", "error");
    }
    // 3. validate against the price after any manual discount (universal = always percentage)
    const isPercent = itemCouponIsPercent(c, isSpecific);
    const value = toNum(c.discount);
    const maxDiscount = isPercent ? toNum(c.maximum_discount ?? c.maximumDiscount) : 0;
    const { couponAmt, reducedPrice } = computeItemCouponForApply(line, { isPercent, value, maxDiscount });
    if (couponAmt > reducedPrice) {
      return ToastifyAlert("Coupon amount is greater than the item price.", "error");
    }
    // 4. store the coupon; the slice recomputes the combined per-unit discount.
    // QV Altria (Android 2026-08-20): the coupon's own altria_funded_discount flag is captured on the
    // line here — order-level coupons no longer carry it, and item_ses.altria_discount_desc.coupon
    // reports THIS coupon. The flag ships on the register's own coupon endpoint (SELECT *).
    const altriaFunded = String(c.altria_funded_discount ?? c.altriaFundedDiscount ?? "0") === "1" ? "1" : "0";
    // Altria LFP promotion code (AGDC field 29): only a funded coupon can carry one.
    const promotionCode = altriaFunded === "1" ? String(c.promotion_code ?? "").trim() : "";
    dispatch(
      applyItemCoupon({
        orderId,
        coupon: { name: c.name, type: isPercent ? "percentage" : "amount", value, maxDiscount, canCombine, altriaFunded, promotionCode },
      })
    );
    ToastifyAlert(`Applied ${c.name}`, "success");
    if (onApplied) onApplied();
    onClose();
  };

  // Same search behaviour Android wired into the register's item-coupon dialog: filter by code,
  // discount value or max-discount as the operator types. A store can carry dozens of coupons and
  // the dialog was previously scroll-only.
  const q = search.trim().toLowerCase();
  const shown = Array.isArray(list)
    ? q
      ? list.filter((c) =>
          [c.name, c.discount, c.maximum_discount ?? c.maximumDiscount]
            .map((v) => String(v == null ? "" : v).toLowerCase())
            .some((v) => v.includes(q))
        )
      : list
    : null;

  return (
    <Modal title="Apply Coupon" onClose={onClose} width={460} zIndex={2100}>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search coupons"
          style={{ flex: 1, height: 40, borderRadius: 10, border: "1px solid var(--line)", padding: "0 12px", fontSize: 14, outline: "none" }}
        />
        {search && (
          <button style={{ border: "1px solid var(--line)", background: "var(--panel-2)", borderRadius: 10, padding: "0 12px", cursor: "pointer" }} onClick={() => setSearch("")}>
            ✕
          </button>
        )}
      </div>
      <div style={{ maxHeight: 360, overflow: "auto" }}>
        {list === null && <div style={{ color: "var(--ink-3)", padding: 16, textAlign: "center" }}>Loading…</div>}
        {Array.isArray(shown) && shown.length === 0 && (
          <div style={{ color: "var(--ink-3)", padding: 16, textAlign: "center" }}>{q ? "No matching coupons" : "No coupons"}</div>
        )}
        {Array.isArray(shown) &&
          shown.map((c) => {
            const isPercent = String(c.flag) === "0" || String(c.type) === "1";
            const cap = toNum(c.maximum_discount ?? c.maximumDiscount);
            return (
              <div
                key={String(c.id) || String(c.name)}
                onClick={() => choose(c)}
                style={{ padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 10, marginBottom: 8, cursor: "pointer" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontWeight: 500 }}>{c.name}</span>
                  {/* item coupons show just "X% OFF" / "$X OFF" — no minimum-order text (Android) */}
                  <span style={{ color: "var(--accent)", fontWeight: 500 }}>{itemCouponLabel(c)}</span>
                </div>
                <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                  {String(c.type) === "1" ? "Universal" : "Specific items"}
                  {isPercent && cap > 0 ? ` · Max Discount $${cap}` : ""}
                  {c.date_expire ? ` · Valid till ${String(c.date_expire).slice(0, 10)}` : ""}
                </div>
              </div>
            );
          })}
      </div>
    </Modal>
  );
}
